<script setup lang="ts">
import type { AnyItem } from '#layers/data-model';
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';

import { computed, ref } from 'vue';
import { RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT } from '#shared/constants/related-values';
import { useRelatedValuesDropdownItems } from './use-related-values-dropdown-items';

const props = defineProps<{
	field: ResolvedMonospaceSingleRelationField;
	/**
	 * Item that the relation field is located on
	 */
	item: AnyItem;
	/**
	 * Related items
	 */
	relatedItems: AnyItem[];
}>();

const open = ref(false);

/**
 * The related items array is only ever a capped slice of the true total.
 * Once it exceeds the cap we only know there are "more than the cap",
 * never the exact total — so show "cap+".
 */
const isOverCap = computed(() => props.relatedItems.length > RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT);

const { dropdownItems, displayTemplate } = useRelatedValuesDropdownItems({
	enabled: open,
	field: () => props.field,
	item: () => props.item,
	relatedItems: () => props.relatedItems,
});
</script>

<template>
	<DDropdownMenu
		v-model:open="open"
		:items="dropdownItems"
		:ui="{
			content: 'w-fit max-w-96',
			item: 'flex h-9 items-center gap-1.5 px-2',
			itemWrapper: 'group h-full justify-center',
			itemLabel: `flex h-full items-center`,
		}"
	>
		<template #default="{ open: isOpen }">
			<DButton
				variant="ghost"
				color="neutral"
				class="-mx-2.5 h-lh py-0 leading-[inherit] font-normal text-default"
				:pressed="isOpen"
			>
				{{ isOverCap ? $t('common.n_plus_items', { n: RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT }) : $t('common.n_items', relatedItems.length) }}
			</DButton>
		</template>

		<template #view-more-label>
			{{ $t('common.view_more') }}
		</template>

		<template #view-more-trailing>
			<DIcon name="lucide:arrow-up-right" />
		</template>

		<template #item-label="{ item: dropdownItem }">
			<RenderCollectionDisplayTemplate
				v-if="'value' in dropdownItem"
				:collection-id="field.oppositeCollectionId"
				:display-template
				:item="dropdownItem.value"
				:root="false"
				class="h-full"
			/>
		</template>

		<template #item-trailing="{ item: dropdownItem }">
			<DIcon
				v-if="'to' in dropdownItem && dropdownItem.to"
				name="lucide:arrow-up-right"
				class="invisible group-hover:visible"
			/>
		</template>
	</DDropdownMenu>
</template>
